import {
  Dispatch,
  HTMLAttributes,
  MouseEventHandler,
  SetStateAction
} from "react";
import { Autocomplete, TextField } from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import { useTranslation } from "react-i18next";
import { OptionCard } from "../../../common-ui/OptionCard";
import { AvailableActivityType } from "../../Activities/activitiesAPI";

type SponsorCardProps = {
  activityType: AvailableActivityType;
  props?: HTMLAttributes<HTMLLIElement>;
  selected?: boolean;
  setSelectedActivityType: Dispatch<
    SetStateAction<AvailableActivityType | undefined>
  >;
  clearable?: boolean;
  disabled?: boolean;
  isMenuItem?: boolean;
  autoCompleteOnClick?: MouseEventHandler<HTMLLIElement> | undefined;
};

const SponsorCard = ({
  activityType,
  props,
  selected,
  setSelectedActivityType,
  clearable,
  disabled,
  isMenuItem,
  autoCompleteOnClick
}: SponsorCardProps) => {
  const { activityLabel, sponsorId, sponsorLogoUrl } = activityType;
  return (
    <OptionCard
      avatarImage={sponsorLogoUrl}
      avatarContent={<BadgeIcon />}
      passedProps={props}
      title={activityLabel ?? "N/A"}
      selected={selected}
      setOption={setSelectedActivityType}
      clearable={clearable}
      disabled={disabled}
      isMenuItem={isMenuItem}
      autoCompleteOnClick={autoCompleteOnClick}
    />
  );
};

type SponsorSelectProps = {
  availableActivityTypes: AvailableActivityType[];
  activityType?: AvailableActivityType;
  setSelectedActivityType: Dispatch<
    SetStateAction<AvailableActivityType | undefined>
  >;
  disabled?: boolean;
};

export const SponsorSelect = ({
  availableActivityTypes,
  activityType,
  setSelectedActivityType,
  disabled
}: SponsorSelectProps) => {
  const { t } = useTranslation();
  if (activityType) {
    return (
      <SponsorCard
        activityType={activityType}
        setSelectedActivityType={setSelectedActivityType}
        selected
        clearable
        disabled={disabled}
      />
    );
  }

  return (
    <Autocomplete
      options={availableActivityTypes}
      disabled={disabled}
      id="sponsor-select-input"
      getOptionLabel={(option) => option.activityLabel ?? "N/A"}
      renderOption={(props, option) => {
        return (
          <SponsorCard
            key={option.sponsorId}
            activityType={option}
            props={props}
            setSelectedActivityType={setSelectedActivityType}
            isMenuItem
            autoCompleteOnClick={props.onClick}
          />
        );
      }}
      renderInput={(params) => {
        return <TextField {...params} label={t("Search Activity Type")} />;
      }}
      onChange={(event, value, reason) => {
        if (reason === "selectOption" && value) {
          setSelectedActivityType(value);
        }
      }}
    />
  );
};
